import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../services/api";
import {
  LayoutDashboard,
  BookOpen,
  PlayCircle,
  Sparkles,
  Film,
  Video,
  Library,
  ClipboardCheck,
  FileText,
  Compass,
  TerminalSquare,
  MessageSquare,
  Target,
  Trophy,
  BarChart3,
  ShieldCheck,
  BriefcaseBusiness,
  Briefcase,
  WalletCards,
  Gift,
  Bell,
  Calendar,
  User,
  HelpCircle,
  Users,
  Users2,
  ChevronRight,
  Mic,
  Flame,
  Brain,
  FileSignature
} from "lucide-react";
import { motion } from "framer-motion";

export default function Sidebar({ role }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarSettings, setSidebarSettings] = useState({ student: {}, teacher: {} });

  useEffect(() => {
    if (role === "student" || role === "teacher") {
      api.get("/settings/sidebar_visibility")
        .then(res => {
          if (res.data) setSidebarSettings(res.data);
        })
        .catch(err => console.error("Failed to fetch sidebar settings"));
    }
  }, [role]);

  const isVisible = (label) => {
    if (role !== "student" && role !== "teacher") return true;
    return sidebarSettings[role]?.[label] !== false;
  };

  // Structure of the Unified Student Sidebar Map (Mirrors Flutter mobile 1-to-1)
  // We use unique query parameters (?tab=...) to distinguish items that share a root page.
  const studentMenuGroups = [
    {
      groupId: "core",
      items: [
        { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
        { label: "My Courses", icon: BookOpen, path: "/dashboard?tab=my-courses" },
      ]
    },
    {
      groupId: "utilities",
      items: [
        { label: "Notifications", icon: Bell, path: "/dashboard?tab=notifications" },
      ]
    },
    {
      groupId: "meta",
      items: [
        { label: "Profile", icon: User, path: "/profile" },
      ]
    }
  ];

  // Keep fallback structures for non-student roles (Admins/Teachers/Parents)
  const adminItems = [
    { label: "Platform Console", icon: LayoutDashboard, path: "/admin" },
    { label: "Manage Institutes", icon: ShieldCheck, path: "/admin/institutes" },
    { label: "SaaS Plans", icon: WalletCards, path: "/admin/saas-plans" },
    { label: "Global Users", icon: Users, path: "/admin/users" },
    { label: "Platform Revenue", icon: BarChart3, path: "/admin/analytics" },
    { label: "Platform Settings", icon: Target, path: "/admin/settings" },
  ];

  const teacherItems = [
    { label: "Dashboard", icon: LayoutDashboard, path: "/teacher-dashboard" },
    { label: "My Courses", icon: BookOpen, path: "/teacher-dashboard?tab=courses" },
    { label: "Profile", icon: User, path: "/profile" },
  ];


  const parentItems = [
    { label: "Parent Dashboard", icon: LayoutDashboard, path: "/parent-dashboard" },
    { label: "My Children", icon: Users, path: "/parent-dashboard" },
  ];

  const renderButton = (item) => {
    // Compare against the full combined route (path + query search params) 
    // to strictly highlight only ONE active sidebar tab.
    const currentFullPath = location.pathname + location.search;
    const isActive = currentFullPath === item.path;

    return (
      <button
        key={item.label}
        onClick={() => {
          if (item.path !== "#") {
            navigate(item.path);
          } else {
            alert(`${item.label} functionality is coming soon!`);
          }
        }}
        className={`group relative w-full flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm font-semibold transition-all ${isActive
          ? "bg-indigo-600 text-white shadow-md shadow-indigo-600/10"
          : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-900 hover:text-slate-900 dark:hover:text-white"
          }`}
      >

        <item.icon size={18} className={`transition-transform ${isActive ? "" : "group-hover:scale-105"}`} />
        <span className="truncate">{item.label}</span>
        {isActive && (
          <motion.div
            layoutId="sidebar-active-indicator"
            className="ml-auto w-1.5 h-1.5 rounded-full bg-white"
          />
        )}
      </button>
    );
  };

  return (
    <aside className="hidden lg:flex fixed left-0 top-16 h-[calc(100vh-64px)] w-64 flex-col border-r border-slate-200 bg-white/95 p-4 shadow-sm backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/95">
      <div className="flex-1 overflow-y-auto pr-2 space-y-4">

        {/* IF ROLE IS STUDENT: Render the 8 structured categories requested by the user */}
        {(role === "student" || !role) && (
          studentMenuGroups.map((group, gIndex) => (
            <React.Fragment key={group.groupId}>
              <div className="space-y-1">
                {group.items
                  .filter(item => isVisible(item.label))
                  .map(item => renderButton(item))}
              </div>
              {gIndex < studentMenuGroups.length - 1 && (
                <div className="my-2 border-t border-slate-100 dark:border-slate-800/60 mx-2" />
              )}
            </React.Fragment>
          ))
        )}

        {/* IF ROLE IS TEACHER */}
        {role === "teacher" && (
          <div className="space-y-1">
            {teacherItems.filter(item => isVisible(item.label)).map(item => renderButton(item))}
          </div>
        )}

        {/* IF ROLE IS PARENT */}
        {role === "parent" && (
          <div className="space-y-1">
            {parentItems.map(item => renderButton(item))}
          </div>
        )}

        {/* IF ROLE IS ADMIN */}
        {role === "admin" && (
          <div className="space-y-1">
            {adminItems.map(item => renderButton(item))}
          </div>
        )}

      </div>
    </aside>
  );
}
